import qrcode from 'qrcode-generator';

interface QRCodeDisplayProps {
  value: string;
  size?: number;
  title?: string;
  className?: string;
  includeMargin?: boolean;
}

/**
 * QRCodeDisplay - A reusable component for displaying QR codes
 *
 * Features:
 * - SVG-based for crisp rendering at any size
 * - Print-friendly
 * - Mobile-scannable
 * - Responsive sizing
 */
export function QRCodeDisplay({
  value,
  size = 256,
  title,
  className = '',
  includeMargin = true,
}: QRCodeDisplayProps) {
  // Generate QR code
  const qr = qrcode(0, 'H'); // Type 0 (auto), error correction level H (high)
  qr.addData(value);
  qr.make();

  // Calculate cell size based on desired total size
  const moduleCount = qr.getModuleCount();
  const cellSize = Math.floor(size / moduleCount);
  const actualSize = cellSize * moduleCount;
  const margin = includeMargin ? cellSize * 2 : 0;
  const totalSize = actualSize + margin * 2;

  // Generate SVG path
  let path = '';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (qr.isDark(row, col)) {
        const x = col * cellSize + margin;
        const y = row * cellSize + margin;
        path += `M${x},${y}h${cellSize}v${cellSize}h-${cellSize}z `;
      }
    }
  }

  return (
    <div className={`qr-code-container ${className}`}>
      {title && (
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
          {title}
        </p>
      )}

      <div className="bg-white p-4 rounded-lg inline-block shadow-sm">
        <svg
          width={totalSize}
          height={totalSize}
          viewBox={`0 0 ${totalSize} ${totalSize}`}
          className="qr-code"
        >
          <rect width={totalSize} height={totalSize} fill="white" />
          <path d={path} fill="black" />
        </svg>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center max-w-xs">
        Scan with your phone's camera to open
      </p>
    </div>
  );
}

export default QRCodeDisplay;
